import { permissionsForRole } from "./catalogue.js";
/**
 * As chaves que o console liga por conta (F169).
 *
 * Um registro, e não strings soltas: o console desenha os interruptores a
 * partir daqui, e o produto lê `PageEntry.feature` daqui — as duas pontas não
 * têm como discordar sobre o nome de uma chave.
 *
 * `marca-propria` é a primeira, e existe de verdade desde o primeiro dia: ela
 * liga a tela `/aparencia`. Um mecanismo cujo primeiro usuário só chegaria na
 * feature seguinte seria código sem chamador.
 */
export const TENANT_FEATURES = [
    {
        key: 'marca-propria',
        label: 'Marca própria',
        description: 'A loja sobe a logo dela e escolhe a cor do sistema, em Configurações → Aparência.',
    },
    /*
     * A primeira vertical (F170, ADR-0038): a fila de produção de quem fabrica
     * uniforme sob medida — a ficha de cada pedido, a grade de tamanhos, a
     * etapa e em qual impressora saiu.
     */
    {
        key: 'uniformes',
        label: 'Uniformes sob medida',
        description: 'A fila de produção com a grade de tamanhos, a etapa de cada pedido e as impressoras, em Produção.',
    },
    /*
     * A loja online que lê o Aeris (F221, ADR-0041): o lojista entra com o
     * número do cadastro e vê o catálogo com o preço e o estoque da filial.
     */
    {
        key: 'loja-online',
        label: 'Loja online',
        description: 'O site de atacado lê o catálogo, o preço e o estoque daqui, e o lojista entra com o número do cadastro.',
    },
    /*
     * O assistente (F251–F255): o dono e os gerentes perguntam pelo WhatsApp do
     * assistente ou pelo Claude, e recebem o resumo do dia.
     */
    {
        key: 'assistente',
        label: 'Assistente (WhatsApp e Claude)',
        description: 'O dono e os gerentes perguntam os números da loja pelo WhatsApp do assistente ou pelo Claude, e recebem o resumo do dia.',
    },
    /*
     * As rotas de entrega (F265–F267): o cliente vira ponto no mapa, o caixa
     * monta a saída dos motoboys e do entregador a pé, e o motoboy recebe a
     * rota. Usa o Google Maps da Plataforma, que é cobrado por chamada.
     */
    {
        key: 'entregas-rota',
        label: 'Rotas de entrega',
        description: 'O caixa monta a rota dos motoboys e da entrega a pé no mapa, com a ordem e o tempo de cada parada, e o motoboy recebe pronta.',
    },
    /*
     * A assistência técnica (F321, ADR-0048): a ordem de serviço do Aeris Tech,
     * com front próprio fora do monorepo. Nenhuma tela do site declara a chave:
     * quem barra é `requireFeature('assistencia')` em cada rota.
     */
    {
        key: 'assistencia',
        label: 'Assistência técnica (Aeris Tech)',
        description: 'Ordens de serviço de conserto: entrada com checklist, orçamento aprovado pelo cliente, andamento, entrega e termo de garantia.',
    },
    /*
     * A climatização (F331, ADR-0049): a visita do refrigerista no Aeris Bot,
     * com front próprio fora do monorepo. Como a assistência, nenhuma tela do
     * site declara a chave: quem barra é `requireFeature('climatizacao')`.
     */
    {
        key: 'climatizacao',
        label: 'Climatização (Aeris Bot)',
        description: 'Visitas de ar-condicionado e refrigeração: equipamento do cliente, orçamento, agenda, relatório técnico com medições e fotos, preventiva e PMOC.',
    },
    /*
     * O ponto dos funcionários (F339, ADR-0050): controle interno do gerente,
     * não o REP-P da Portaria 671. A tela Ponto declara a chave.
     */
    {
        key: 'ponto',
        label: 'Ponto dos funcionários',
        description: 'O funcionário bate o ponto com código e foto, no tablet da loja ou no próprio celular, e o gerente vê quem está na loja.',
    },
    /*
     * O balcão como era antes das F360–F366 (29/10). O dono: "a 4Play tem que
     * ficar igual e o resto mudar". Ligada só na 4Play: a Início do vendedor
     * com o total da loja, a venda sem os códigos, o código da loja só no
     * cancelar, o PDV sem "Venda com troca" e Relatórios sem as variações.
     */
    {
        key: 'balcao-anterior',
        label: 'Balcão como antes (29/10)',
        description: 'Mantém o balcão como era: vendedor vê o total da loja, código da loja só para cancelar, sem "Venda com troca" no PDV e sem o relatório de variações.',
    },
];
export const isTenantFeature = (value) => TENANT_FEATURES.some((entry) => entry.key === value);
/**
 * A chave está ligada nesta conta?
 *
 * Recebe `Iterable<string>` e não `Iterable<TenantFeature>` porque o que chega
 * do banco é texto: a coluna é `text` de propósito (o mesmo motivo da migração
 * 0030), e uma chave que este código ainda não conhece tem de ser ignorada, não
 * derrubar a sessão.
 */
const enabled = (features, page) => page.feature === undefined || new Set(features).has(page.feature);
export const PAGES = [
    { module: 'inicio', label: 'Início' },
    /*
     * A ajuda não é negável (F156).
     *
     * É onde mora o glossário: o que "Saiu" quer dizer, o que "Convertido" fez
     * com o orçamento, por que um selo está verde. Tirar isso de alguém é tirar
     * a chance de essa pessoa entender a tela que ela usa — e quem mais precisa
     * é justamente quem tem menos telas.
     */
    { module: 'ajuda', label: 'Ajuda' },
    /*
     * O PDV lê de três telas que o admin pode tirar (F152).
     *
     * Mesma classe do 403 do trocador (F150): a permissão efetiva só sobrevive
     * enquanto resta uma tela que a declare, e o PDV declara `sales.write`. Tirar
     * **Produtos** do vendedor fazia o campo de bipagem parar de achar produto;
     * tirar **Clientes**, o seletor de cliente; tirar **Preços**, a tabela de
     * preço da venda. Nos três casos o balcão PARA, e a causa fica numa caixinha
     * marcada em outra tela.
     *
     * **Crédito (`finance.read`) e vale (`exchange.read`) ficaram de fora, e não
     * por esquecimento.** Sem eles o PDV não para: ele já esconde os dois blocos
     * com `allows(...)` e vende do mesmo jeito. Declará-los aqui faria "tirar o
     * Financeiro de alguém" deixar de revogar a leitura do financeiro — uma
     * decisão que já está escrita em teste, e que não é desta feature reverter.
     * O preço de deixar fora: quem não alcança nenhuma tela de financeiro não vê
     * o limite de crédito no caixa. Se isso incomodar na loja, é uma decisão do
     * dono, não um conserto silencioso.
     */
    {
        module: 'pdv',
        label: 'PDV',
        permission: 'sales.write',
        uses: ['catalog.read', 'party.read', 'pricing.read'],
    },
    { module: 'caixa', label: 'Caixa', permission: 'pos.operate' },
    /*
     * As duas leem o pagamento para reimprimir a nota (F150).
     *
     * Sem isto, tirar "Formas de pagamento" de alguém faria a nota reimpressa
     * sair sem a forma de pagamento e sem o troco — o papel mudaria por causa de
     * uma caixinha marcada noutra tela, e ninguém ligaria uma coisa à outra.
     */
    {
        module: 'vendas',
        label: 'Vendas',
        permission: 'sales.read',
        uses: ['payment.read'],
    },
    {
        module: 'orcamentos',
        label: 'Orçamentos',
        permission: 'sales.read',
        uses: ['payment.read'],
    },
    {
        module: 'atendimento',
        label: 'Atendimento',
        permission: 'communication.conversation.read',
    },
    /*
     * O acerto do motoboy lê as FORMAS de pagamento (F179).
     *
     * O dono mandou o print: "Como pagou:" e **nenhum botão** embaixo. Não era
     * tela quebrada — era o mesmo 403 silencioso do trocador (F150): o admin
     * tirou Formas de pagamento do caixa, e a linha ficou sem nada para
     * oferecer, com a causa a duas telas de distância do sintoma.
     *
     * `exchange.read` NÃO entra, e a ausência é escolhida: o saldo de vale
     * chega dentro da própria resposta das entregas (F179), e declará-lo aqui
     * faria a conta que desliga Trocas continuar com a leitura da troca viva —
     * exatamente o que `pages.test.ts` proíbe. Loja sem Trocas não tem vale, e
     * a linha simplesmente não oferece o botão.
     */
    {
        module: 'entregas',
        label: 'Acerto do motoboy',
        permission: 'finance.read',
        /*
         * `sales.read` entra na F189, e fecha um 403 que ainda não tinha
         * acontecido: a tela abre com `finance.read`, e a lista de entregas é
         * `GET /sales/deliveries`, que exige `sales.read`. Hoje os quatro papéis
         * com `finance.read` também têm `sales.read`, então ninguém tropeçou —
         * mas o admin que tirasse Vendas do caixa deixaria o acerto abrindo numa
         * tela vazia, com a causa a duas telas de distância. É exatamente o que
         * este campo existe para impedir (F150, F152, F179).
         */
        uses: ['payment.read', 'sales.read'],
    },
    /*
     * As rotas de entrega (F265–F267), atrás da chave `entregas-rota`. Na F265
     * a tela localiza os clientes no mapa e guarda a saída da filial.
     */
    {
        module: 'rotas',
        label: 'Rotas de entrega',
        permission: 'delivery.route.read',
        feature: 'entregas-rota',
    },
    /* O ponto dos funcionários (F339), atrás da chave `ponto`. */
    {
        module: 'ponto',
        label: 'Ponto',
        permission: 'timeclock.read',
        feature: 'ponto',
    },
    /*
     * A fila de produção de uniformes (F170) — só para a conta com a chave.
     *
     * A fila mostra valor e cliente, cria o pedido pelo formulário de vendas
     * (que busca produto, resolve preço e escolhe vendedor) e repete pedido: é o
     * mesmo raciocínio de `trocas`, e sem os `uses` quem perdesse Vendas
     * perderia a fila junto. `sales.write` continua dentro do papel — o caixa,
     * que não a tem, não ganha nada por isto.
     */
    {
        module: 'producao',
        label: 'Produção',
        permission: 'uniforms.read',
        uses: [
            'sales.read',
            'sales.write',
            'party.read',
            'catalog.read',
            'pricing.read',
        ],
        feature: 'uniformes',
    },
    { module: 'produtos', label: 'Produtos', permission: 'catalog.read' },
    { module: 'precos', label: 'Preços', permission: 'pricing.read' },
    { module: 'estoque', label: 'Estoque', permission: 'inventory.read' },
    { module: 'aplicacoes', label: 'Aplicações', permission: 'catalog.read' },
    { module: 'importar', label: 'Importar', permission: 'catalog.write' },
    {
        module: 'importar-fotos',
        label: 'Importar fotos',
        permission: 'catalog.write',
    },
    {
        module: 'importar-clientes',
        label: 'Importar clientes',
        permission: 'party.write',
    },
    {
        module: 'importar-nfe',
        label: 'Importar NF-e',
        permission: 'purchasing.write',
    },
    { module: 'compras', label: 'Compras', permission: 'purchasing.read' },
    { module: 'financeiro', label: 'Financeiro', permission: 'finance.read' },
    { module: 'cobrancas', label: 'Cobranças', permission: 'finance.read' },
    { module: 'comissoes', label: 'Comissões', permission: 'commission.read' },
    { module: 'gastos', label: 'Folha e gastos', permission: 'expense.read' },
    // Quanto entrou em Pix pelo atendimento (F205). O caixa confere; ninguém dá baixa aqui.
    { module: 'pix', label: 'Pix recebidos', permission: 'pix.charge.read' },
    { module: 'pessoas', label: 'Clientes', permission: 'party.read' },
    {
        module: 'pedidos-canal',
        label: 'Pedidos dos canais',
        permission: 'channel.read',
    },
    /*
     * O pedido da loja online (F224): a vendedora aprova ou recusa. Quem vende
     * vê — `sales.read`; decidir pede `sales.write`, conferido na rota.
     */
    {
        module: 'pedidos-loja',
        label: 'Pedidos da loja',
        permission: 'sales.read',
        feature: 'loja-online',
    },
    { module: 'marcadores', label: 'Marcadores', permission: 'tag.read' },
    {
        module: 'relatorios',
        label: 'Relatórios',
        permission: 'report.read',
        // O seletor de filial da barra. Sem declarar, o gerente que perdeu a tela
        // da Empresa ficaria sem o filtro — e o relatório passaria a somar a rede
        // inteira sem que ninguém tivesse pedido isso.
        uses: ['organization.read'],
    },
    /*
     * O cadastro de marcas (F158).
     *
     * `catalog.write`, e não `read`: a tela é para ADMINISTRAR o vocabulário.
     * Quem só consulta produto continua vendo as marcas onde importa — no seletor
     * do formulário, que lê pela `catalog.read` que ele já tem.
     */
    { module: 'marcas', label: 'Marcas', permission: 'catalog.write' },
    {
        module: 'trocas',
        label: 'Trocas e vales',
        permission: 'exchange.read',
        // A venda original, o cliente (produto de fora, F148) e a busca de produto.
        uses: ['sales.read', 'party.read', 'catalog.read'],
    },
    { module: 'etiquetas', label: 'Etiquetas', permission: 'catalog.read' },
    {
        module: 'modelo-de-etiqueta',
        label: 'Modelo de etiqueta',
        permission: 'catalog.read',
    },
    {
        module: 'modelo-de-nota',
        label: 'Modelo da nota',
        permission: 'organization.write',
        // A tela lê a empresa para mostrar o cabeçalho na prévia (F149).
        uses: ['organization.read'],
    },
    /*
     * A primeira tela de chave do produto (F169).
     *
     * É ela que faz o mecanismo de telas por conta nascer com um usuário real,
     * em vez de esperar o primeiro cliente de vertical: a loja sem
     * `marca-propria` não vê Aparência em lugar nenhum, e a rota recusa.
     */
    {
        module: 'aparencia',
        label: 'Aparência',
        permission: 'organization.write',
        feature: 'marca-propria',
    },
    /*
     * A loja online (F221): o endereço, a filial e as sessões abertas dos
     * lojistas. Só existe na conta com a chave `loja-online`.
     */
    {
        module: 'loja-online',
        label: 'Loja online',
        permission: 'storefront.manage',
        feature: 'loja-online',
    },
    /*
     * O assistente (F251–F255). "Assistente" configura e lê o histórico de
     * todos; "Meu assistente" liga o WhatsApp da pessoa e conecta o Claude. Por
     * serem telas, o admin pode tirar o assistente de um gerente só (F038).
     */
    {
        module: 'assistente',
        label: 'Assistente',
        permission: 'assistant.manage',
        feature: 'assistente',
    },
    {
        module: 'meu-assistente',
        label: 'Meu assistente',
        permission: 'assistant.use',
        feature: 'assistente',
    },
    /*
     * O cadastro das impressoras da produção (F170). `uniforms.manage` é só dela:
     * negar esta aba a alguém não mexe na troca de etapa, que é `uniforms.write`
     * e não é declarada por tela nenhuma.
     */
    {
        module: 'impressoras',
        label: 'Impressoras da produção',
        permission: 'uniforms.manage',
        feature: 'uniformes',
    },
    /*
     * A função de cada pessoa na produção (F171). Declara a mesma
     * `uniforms.manage` das impressoras: tirar só uma das duas abas não tira o
     * "mexe em tudo" — tirar as duas, sim.
     */
    {
        module: 'equipe-producao',
        label: 'Equipe da produção',
        permission: 'uniforms.manage',
        feature: 'uniformes',
    },
    {
        module: 'formas-de-pagamento',
        label: 'Formas de pagamento',
        permission: 'payment.read',
    },
    { module: 'terminais', label: 'Terminais', permission: 'pos.operate' },
    { module: 'canais', label: 'Canais', permission: 'channel.read' },
    {
        module: 'atendimento-canais',
        label: 'Canais de atendimento',
        permission: 'communication.channel.read',
    },
    {
        module: 'atendimento-guia',
        label: 'Guia de configuração',
        permission: 'communication.channel.read',
    },
    {
        module: 'atendimento-grupos',
        label: 'Grupos do WhatsApp',
        permission: 'communication.group.manage',
    },
    {
        module: 'atendimento-filas',
        label: 'Filas de atendimento',
        permission: 'communication.queue.read',
    },
    {
        module: 'atendimento-modelos',
        label: 'Modelos',
        permission: 'communication.template.read',
    },
    {
        module: 'chamados',
        label: 'Chamados',
        permission: 'communication.ticket.read',
    },
    {
        module: 'atendimento-robo',
        label: 'Robô de atendimento',
        permission: 'communication.bot.read',
    },
    {
        module: 'atendimento-robo-preco',
        label: 'Robô de preço',
        permission: 'communication.bot.read',
    },
    {
        module: 'atendimento-pos-venda',
        label: 'Pós-venda',
        permission: 'communication.followup.read',
    },
    {
        module: 'crm-regras',
        label: 'Regras de follow-up',
        permission: 'communication.followup.read',
    },
    {
        module: 'crm-campanhas',
        label: 'Campanhas',
        permission: 'communication.campaign.read',
    },
    {
        module: 'atendimento-custos',
        label: 'Custos de atendimento',
        permission: 'communication.cost.read',
    },
    // A carga de cada atendente e a distribuição (F210): de quem supervisiona.
    {
        module: 'atendimento-painel',
        label: 'Painel do atendimento',
        permission: 'communication.queue.write',
    },
    { module: 'empresa', label: 'Empresa', permission: 'organization.write' },
    { module: 'equipe', label: 'Equipe', permission: 'identity.user.read' },
    { module: 'edge', label: 'Máquinas da loja', permission: 'edge.manage' },
    { module: 'baixar', label: 'Aeris no computador' },
    /*
     * As duas páginas de configuração (F068), sem permissão — como `baixar`.
     *
     * Elas não mostram nada: mandam para a primeira aba que a pessoa pode abrir.
     * Quem decide o que ela vê são as entradas acima, uma por aba, que
     * continuam intactas — é o que preserva "o admin nega Custos de atendimento
     * sem negar o robô junto", e o que `effectivePermissions` continua usando
     * para derivar o efetivo.
     *
     * Declarar uma permissão aqui seria pior de duas formas: colapsaria sete
     * telas numa, e faria `effectivePermissions` conceder a permissão dessa
     * página a quem só devia alcançar uma aba.
     */
    { module: 'atendimento-config', label: 'Configurações do atendimento' },
    { module: 'configuracoes', label: 'Configurações da loja' },
];
/**
 * As telas que o admin **não** pode tirar de ninguém.
 *
 * `inicio` é para onde o login manda a pessoa. Sem ela, entrar leva a lugar
 * nenhum e o suporte recebe um "não abre" sem sintoma.
 */
/**
 * O que o admin não consegue tirar de ninguém.
 *
 * `inicio` porque é para onde todo mundo cai ao entrar; `ajuda` porque é onde
 * o produto se explica (F156), e uma pessoa sem acesso à explicação é uma
 * pessoa que vai ligar para o suporte.
 */
export const UNDENIABLE_PAGES = ['inicio', 'ajuda'];
export const isPage = (value) => PAGES.some((page) => page.module === value);
/** As permissões que alguma tela declara. As outras seguem o papel. */
const PAGE_DECLARED = new Set(PAGES.flatMap((page) => page.permission === undefined ? [] : [page.permission]));
/**
 * O que a pessoa pode fazer, depois de o admin tirar telas dela.
 *
 * ```
 * ⋃ { permissões das telas que ela mantém }  ∩  permissões do papel
 * ```
 *
 * ## Por que união, e não subtração
 *
 * `catalog.read` é exigida por Produtos **e** pelo PDV. Se tirar Produtos
 * revogasse `catalog.read` direto, o PDV dessa pessoa pararia de encontrar
 * produto para vender — e o sintoma apareceria longe da causa, num balcão com
 * fila. Uma permissão só se perde quando a última tela que a usa é tirada.
 *
 * ## Por que o papel é o teto
 *
 * A tela do admin **tira**, dentro do que o papel já permitia; ela não concede.
 * Um caixa não ganha o financeiro porque alguém marcou uma caixinha — para isso
 * muda-se o papel, que é onde a hierarquia de `authority.ts` sabe opinar.
 *
 * As permissões que nenhuma tela declara — `sales.cancel`, `pos.supervise`,
 * `inventory.adjust`, as que governam um botão dentro de uma tela — seguem o
 * papel, intocadas. Esta feature não é sobre elas.
 */
export const effectivePermissions = (role, deniedPages, features) => {
    const denied = new Set(deniedPages);
    const granted = new Set(permissionsForRole(role));
    const reachable = new Set();
    for (const page of PAGES) {
        if (denied.has(page.module))
            continue;
        /*
         * A tela de uma chave desligada não alcança nada (F169).
         *
         * Sem esta linha, uma permissão declarada SÓ por uma tela de vertical seria
         * concedida a toda conta — a tela não apareceria, e a rota responderia.
         */
        if (!enabled(features, page))
            continue;
        if (page.permission !== undefined)
            reachable.add(page.permission);
        // O que a tela lê só conta para quem alcança a tela: sem isso, um caixa sem
        // Trocas manteria `sales.read` depois de perder Vendas e Orçamentos.
        if (page.permission === undefined || granted.has(page.permission)) {
            for (const used of page.uses ?? [])
                reachable.add(used);
        }
    }
    return permissionsForRole(role).filter((permission) => !PAGE_DECLARED.has(permission) || reachable.has(permission));
};
/**
 * As telas que este papel alcança, negações à parte.
 *
 * É o que a tela do admin mostra travado: o que o papel não inclui aparece
 * desmarcado e explicado, em vez de sumir — sumir faria o admin procurar o que
 * não está lá.
 *
 * A chave da conta (F169) é diferente do papel, e por isso some em vez de
 * aparecer travada: uma tela que esta loja não contratou não é algo que o
 * admin dela possa conceder nem entender. Explicar "Atletas e medidas, travado"
 * para uma loja de celulares seria oferecer uma pergunta sem resposta.
 */
export const pagesForRole = (role, features, 
/** As telas que a conta não tem (F172) — somem como as de chave desligada. */
pagesOff = []) => {
    const granted = new Set(permissionsForRole(role));
    const off = new Set(accountPagesOff(pagesOff));
    return PAGES.filter((page) => enabled(features, page) &&
        !off.has(page.module) &&
        (page.permission === undefined || granted.has(page.permission))).map((page) => page.module);
};
/**
 * As telas do núcleo que o console pode desligar numa conta (F172).
 *
 * Todas, menos:
 *
 * - as que ninguém perde (`UNDENIABLE_PAGES`): uma conta sem Início não tem
 *   para onde mandar quem entra;
 * - os contêineres sem permissão (`configuracoes`, `atendimento-config`): são
 *   abas que somem sozinhas quando as abas de dentro somem;
 * - as telas de chave (F169): essas já existem só para quem tem a chave.
 */
export const pagesTenantCanDisable = () => PAGES.filter((page) => !UNDENIABLE_PAGES.includes(page.module) &&
    page.permission !== undefined &&
    page.feature === undefined).map((page) => page.module);
/**
 * As telas que a conta desligou e que de fato podem ser desligadas.
 *
 * O banco guarda texto (0100). Uma linha que não está em
 * `pagesTenantCanDisable` — tela que este código não conhece, ou Início gravado
 * por engano — é ignorada em vez de tirar de todo mundo a porta de entrada.
 */
export const accountPagesOff = (pagesOff) => {
    const allowed = new Set(pagesTenantCanDisable());
    return [...new Set(pagesOff)].filter((page) => allowed.has(page));
};
/**
 * As telas que somem para esta pessoa: as que o lojista tirou dela (F038) e as
 * que a conta não tem (F172). As duas passam pela mesma união de
 * `effectivePermissions` — desligar uma tela na conta é negá-la a todos.
 */
export const hiddenPages = (deniedPages, pagesOff) => [...new Set([...deniedPages, ...accountPagesOff(pagesOff)])];
/**
 * As telas que esta pessoa vê, de fato.
 *
 * ## Por que isto não dá para deduzir das permissões
 *
 * `effectivePermissions` responde "o que ela pode fazer", e o menu perguntava
 * "o que ela vê" olhando aquela resposta. As duas perguntas se separam sempre
 * que **duas telas exigem a mesma permissão**, e o produto tem três casos:
 *
 * | permissão      | telas                     |
 * | -------------- | ------------------------- |
 * | `sales.read`   | Vendas, Orçamentos        |
 * | `catalog.read` | Produtos, Aplicações      |
 * | `pos.operate`  | Caixa, Terminais          |
 *
 * Tirar Vendas de alguém que fica com Orçamentos não pode revogar
 * `sales.read` — o Orçamentos pararia de funcionar, e essa é exatamente a
 * razão da união lá em cima. Só que o menu, lendo a permissão, continuava
 * mostrando Vendas: o admin marcava, gravava, e a tela seguia lá.
 *
 * Foi o que o dono viu no primeiro login do caixa na loja: *"era pra vir só
 * PDV, orçamento, clientes, produtos"*.
 *
 * ## Onde isto vale, e onde não vale
 *
 * Isto é **navegação**, não autorização. Quem fica com Orçamentos mantém
 * `sales.read`, e a API vai continuar respondendo `GET /sales` para essa
 * pessoa — tirar a tela não tranca o dado, esconde o caminho. Trancar o dado é
 * papel do papel: para isso muda-se de `cashier` para `salesperson`, que é
 * onde a hierarquia sabe opinar.
 *
 * A tela do admin diz isso com todas as letras, porque um administrador que
 * acredita ter revogado um acesso que continua alcançável está em situação
 * pior do que um que sabe que não revogou.
 */
export const visiblePages = (role, deniedPages, features) => {
    const denied = new Set(deniedPages);
    return pagesForRole(role, features).filter((page) => UNDENIABLE_PAGES.includes(page) || !denied.has(page));
};
/**
 * O dinheiro que sai da sessão de quem só trabalha na arte e/ou na fábrica
 * da confecção (F220).
 *
 * O vendedor é o papel de todo mundo na 4Play — atendente, designer e
 * operador —, e o papel traz o PDV, as vendas e os preços. Quem a Equipe da
 * produção pôs só na arte ou na fábrica não precisa de nada disso, e o dono
 * não quer que essa pessoa veja o valor dos pedidos. Quem decide quem é essa
 * pessoa é `seesMoney` de `@aeris/uniforms`; aqui fica só O QUE ela perde.
 *
 * As permissões de leitura e de escrita juntas: o PDV e o sinal mostram o
 * preço tanto quanto a lista de vendas.
 */
export const MONEY_PERMISSIONS = [
    'sales.read',
    'sales.write',
    'sales.deposit',
    'sales.price.override',
    'pricing.read',
    'commission.read.own',
    'pix.charge.create',
    'exchange.read',
    'exchange.request',
];
/**
 * O acesso sem o dinheiro: as permissões de `MONEY_PERMISSIONS` saem, e com
 * elas as telas que dependiam de uma delas — senão o menu ofereceria Vendas e
 * a primeira requisição responderia 403.
 */
export const withoutMoney = (access) => {
    const money = new Set(MONEY_PERMISSIONS);
    const permissions = access.permissions.filter((p) => !money.has(p));
    const kept = new Set(permissions);
    const byModule = new Map(PAGES.map((page) => [page.module, page]));
    const pages = access.pages.filter((module) => {
        const required = byModule.get(module)?.permission;
        return required === undefined || kept.has(required);
    });
    return { permissions, pages };
};
//# sourceMappingURL=pages.js.map